import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRentalPendingInvoices, payRentalInvoice } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { printRentalInvoice } from '../../utils/rentalInvoicePrint';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const statusColor = (status) => {
  const map = { pending: 'warning', partial: 'info', overdue: 'error', paid: 'success' };
  return map[status] || 'default';
};

const RentalPendingPayments = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('filter') || 'pending');
  const [payInv, setPayInv] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [payNotes, setPayNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalPendingInvoices({ filter, search });
      setRows(res.data || []);
      setTotals(res.totals || null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load pending rental invoices'));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    load();
    const onBranch = () => load();
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  const savePay = async () => {
    if (!payInv) return;
    try {
      setBusy(true);
      await payRentalInvoice(payInv.id, {
        amount: parseFloat(payAmount),
        payment_method: payMethod,
        payment_notes: payNotes
      });
      setPayInv(null);
      setPayAmount('');
      setPayNotes('');
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Payment failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pending rental payments</Typography>
          <Typography variant="body2" color="text.secondary">
            Unpaid rental invoices, including overdue periods.
          </Typography>
        </Box>
        <ExportButtons
          title="Pending Rental Payments"
          filename="rental_pending_payments"
          columns={[
            { label: 'Invoice', value: (r) => dash(r.invoice_number) },
            { label: 'Rental', value: (r) => dash(r.rental_number) },
            { label: 'Customer', value: (r) => dash(r.customer?.name) },
            { label: 'Due', value: (r) => fmtExportDate(r.due_date) },
            { label: 'Balance', value: (r) => fmtExportMoney(r.balance) },
            { label: 'Status', value: (r) => fmtExportStatus(r.display_status) }
          ]}
          rows={rows}
        />
      </Box>

      {totals && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Shown invoices', value: rows.length },
            { label: 'Pending amount', value: money(totals.pending_amount) },
            { label: 'Overdue invoices', value: totals.overdue_count || 0 },
            { label: 'Overdue amount', value: money(totals.overdue_amount) }
          ].map((card) => (
            <Grid item xs={6} md={3} key={card.label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search invoice, rental, customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="pending">All pending</MenuItem>
          <MenuItem value="overdue">Overdue</MenuItem>
          <MenuItem value="due_soon">Due this week</MenuItem>
          <MenuItem value="partial">Partial</MenuItem>
        </TextField>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Rental / items</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary">No pending rental invoices.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell>{inv.invoice_number}</TableCell>
                  <TableCell>
                    {inv.customer?.name || '—'}
                    {inv.customer?.phone ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {inv.customer.phone}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/rentals/${inv.rental_id}`)}>
                      {inv.rental_number}
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {(inv.items || []).map((it) => it.product_name).join(', ')}
                    </Typography>
                  </TableCell>
                  <TableCell>{inv.period_start} → {inv.period_end}</TableCell>
                  <TableCell>{inv.due_date}</TableCell>
                  <TableCell align="right">{money(inv.balance)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={inv.display_status} color={statusColor(inv.display_status)} />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/rentals/invoices/${inv.id}`)}>
                      View invoice
                    </Button>
                    <Button size="small" onClick={() => printRentalInvoice(inv)}>Print</Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setPayInv(inv);
                        setPayAmount(String(inv.balance || inv.amount));
                      }}
                    >
                      Pay
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(payInv)} onClose={() => setPayInv(null)} fullWidth maxWidth="xs">
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {payInv?.invoice_number} · {payInv?.customer?.name} · Balance {money(payInv?.balance)}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            label="Method"
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)}
            sx={{ mb: 2 }}
          >
            {['Cash', 'Bank Transfer', 'Card', 'Cheque', 'Other'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Notes" value={payNotes} onChange={(e) => setPayNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayInv(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePay} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalPendingPayments;
